/* eslint-disable react-refresh/only-export-components */
/**
 * Document Editing Options Context
 * Manages editing options for document export (DOCX/HWPX) with server-backed sync
 *
 * These are EXPORT-ONLY options applied when generating DOCX/HWPX files,
 * mirroring the desktop app's "편집 옵션" dialog.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  EditingOptions,
  TextbookOptions,
  QuestionExportOptions,
} from '@/types/editingOptions';
import {
  DEFAULT_EDITING_OPTIONS,
  DEFAULT_TEXTBOOK_OPTIONS,
  DEFAULT_QUESTION_EXPORT_OPTIONS,
} from '@/types/editingOptions';
import {
  loadEditingOptions,
  loadEditingOptionsUpdatedAt,
  clearEditingOptions,
  validateEditingOptions,
} from '@/services/editingOptionsService';
import { useAuth } from '@/hooks/useAuth';
import { userPreferencesApi } from '@/services/api';
import { useDocumentSettingsState } from '@/hooks/useDocumentSettingsState';
import { debugEvent } from '@/lib/debugApi';

interface DocumentEditingOptionsContextValue {
  options: EditingOptions;
  updatedAt: number;
  updateTextbookOption: <K extends keyof TextbookOptions>(key: K, value: TextbookOptions[K]) => void;
  updateQuestionOption: <K extends keyof QuestionExportOptions>(key: K, value: QuestionExportOptions[K]) => void;
  resetTextbookOptions: () => void;
  resetQuestionOptions: () => void;
  resetAllOptions: () => void;
  /** Replace the entire EditingOptions object (e.g. when loading a settings preset) */
  replaceOptions: (options: EditingOptions) => void;
  isSyncing: boolean;
  isHydrating: boolean;
  syncError: string | null;
  loadFailed: boolean;
  retrySync: () => void;
}

interface DocumentEditingOptionsProviderProps {
  children: React.ReactNode;
}

const DocumentEditingOptionsContext = createContext<DocumentEditingOptionsContextValue | undefined>(undefined);

export function DocumentEditingOptionsProvider({ children }: DocumentEditingOptionsProviderProps) {
  const { user, isImpersonating } = useAuth();
  const canPersist = !!user && !isImpersonating;

  const [options, setOptionsState, optionsRef] = useDocumentSettingsState<EditingOptions>({ ...DEFAULT_EDITING_OPTIONS });
  const [updatedAt, setUpdatedAt] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isHydrating, setIsHydrating] = useState(!!user);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const scopeRef = useRef(0);
  const loadFailedRef = useRef(false);


  // Hand changes directly to the API queue so navigation cannot cancel a save.
  const syncVersionRef = useRef(0);
  const isHydratingRef = useRef(false);
  const syncToServer = useCallback(async (newOptions: EditingOptions) => {
    if (!canPersist || isHydratingRef.current) return;
    const scope = scopeRef.current;
    const version = ++syncVersionRef.current;
    setIsSyncing(true);
    setSyncError(null);
    try {
      const response = await userPreferencesApi.update({ editingOptions: newOptions });
      if (scope !== scopeRef.current || version !== syncVersionRef.current) return;
      if (response?.editingOptionsUpdatedAt) setUpdatedAt(response.editingOptionsUpdatedAt);
    } catch {
      if (scope === scopeRef.current && version === syncVersionRef.current) {
        setSyncError('변경 사항을 저장하지 못했습니다. 다시 저장해 주세요.');
      }
    } finally {
      if (scope === scopeRef.current && version === syncVersionRef.current) setIsSyncing(false);
    }
  }, [canPersist]);

  // Hydrate from server on login
  useEffect(() => {
    const scope = ++scopeRef.current;
    let cancelled = false;
    setOptionsState({ ...DEFAULT_EDITING_OPTIONS });

    setUpdatedAt(0);
    setSyncError(null);
    setLoadFailed(false);
    loadFailedRef.current = false;
    setIsSyncing(false);
    isHydratingRef.current = !!user;
    setIsHydrating(!!user);
    if (!user) return;

    const hydrateFromServer = async () => {
      setIsHydrating(true);
      isHydratingRef.current = true;

      try {
        const legacyOptions = loadEditingOptions();
        const legacyUpdatedAt = loadEditingOptionsUpdatedAt();
        debugEvent('DocumentEditingOptions.hydrate:start', { updatedAt });
        const serverPrefs = await userPreferencesApi.get();
        if (cancelled || scope !== scopeRef.current) return;

        if (serverPrefs) {
          const serverOptions = serverPrefs.editingOptions as EditingOptions | undefined;
          const serverUpdatedAt = (serverPrefs.editingOptionsUpdatedAt as number) ?? 0;

          if (serverOptions) {
            const validated = validateEditingOptions(serverOptions);
            setOptionsState(validated);
            setUpdatedAt(serverUpdatedAt);
            debugEvent('DocumentEditingOptions.hydrate:use-server', {
              serverUpdatedAt,
              localUpdatedAt: legacyUpdatedAt,
            });
          } else if (!isImpersonating && legacyUpdatedAt > 0) {
            debugEvent('DocumentEditingOptions.hydrate:push-local', {
              localUpdatedAt: legacyUpdatedAt,
            });
            const validated = validateEditingOptions(legacyOptions);
            setOptionsState(validated);
            setUpdatedAt(legacyUpdatedAt);
            await userPreferencesApi.update({
              editingOptions: validated,
            });
          }

          if (cancelled || scope !== scopeRef.current) return;
          if (!isImpersonating && (serverOptions || legacyUpdatedAt > 0)) {
            clearEditingOptions();
          }
        }
      } catch {
        if (cancelled) return;
        setLoadFailed(true);
        loadFailedRef.current = true;
        setSyncError('설정을 불러오지 못했습니다. 다시 불러와 주세요.');
        // Keep local values on failure
        debugEvent('DocumentEditingOptions.hydrate:error');
      } finally {
        if (!cancelled) {
          setIsHydrating(false);
          // A failed load must not let defaults overwrite the saved preferences.
          isHydratingRef.current = false;
          debugEvent('DocumentEditingOptions.hydrate:done');
        }
      }
    };

    void hydrateFromServer();
    return () => { cancelled = true; scopeRef.current = scope + 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isImpersonating, reloadVersion]);

  const updateTextbookOption = useCallback(
    <K extends keyof TextbookOptions>(key: K, value: TextbookOptions[K]) => {
      if (isHydratingRef.current || loadFailedRef.current) return;

      setOptionsState((prev) => {
        const updated: EditingOptions = {
          ...prev,
          textbook: {
            ...prev.textbook,
            [key]: value,
          },
        };
        const validated = validateEditingOptions(updated);
        syncToServer(validated);
        return validated;
      });

      setUpdatedAt(Date.now());
    },
    [setOptionsState, syncToServer]
  );

  const updateQuestionOption = useCallback(
    <K extends keyof QuestionExportOptions>(key: K, value: QuestionExportOptions[K]) => {
      if (isHydratingRef.current || loadFailedRef.current) return;

      setOptionsState((prev) => {
        const updated: EditingOptions = {
          ...prev,
          question: {
            ...prev.question,
            [key]: value,
          },
        };
        const validated = validateEditingOptions(updated);
        syncToServer(validated);
        return validated;
      });

      setUpdatedAt(Date.now());
    },
    [setOptionsState, syncToServer]
  );

  const resetTextbookOptions = useCallback(() => {
    if (isHydratingRef.current || loadFailedRef.current) return;

    setOptionsState((prev) => {
      const updated: EditingOptions = {
        ...prev,
        textbook: { ...DEFAULT_TEXTBOOK_OPTIONS },
      };
      syncToServer(updated);
      return updated;
    });

    setUpdatedAt(Date.now());
  }, [setOptionsState, syncToServer]);

  const resetQuestionOptions = useCallback(() => {
    if (isHydratingRef.current || loadFailedRef.current) return;

    setOptionsState((prev) => {
      const updated: EditingOptions = {
        ...prev,
        question: { ...DEFAULT_QUESTION_EXPORT_OPTIONS },
      };
      syncToServer(updated);
      return updated;
    });

    setUpdatedAt(Date.now());
  }, [setOptionsState, syncToServer]);

  const resetAllOptions = useCallback(() => {
    if (isHydratingRef.current || loadFailedRef.current) return;

    const updated = { ...DEFAULT_EDITING_OPTIONS };
    setOptionsState(updated);
    setUpdatedAt(Date.now());
    syncToServer(updated);
  }, [setOptionsState, syncToServer]);

  const replaceOptions = useCallback(
    (nextOptions: EditingOptions) => {
      if (isHydratingRef.current || loadFailedRef.current) return;

      const validated = validateEditingOptions(nextOptions);
      setOptionsState(validated);
      setUpdatedAt(Date.now());
      syncToServer(validated);
    },
    [setOptionsState, syncToServer]
  );

  const retrySync = useCallback(() => {
    if (loadFailed) setReloadVersion((value) => value + 1);
    else void syncToServer(optionsRef.current);
  }, [loadFailed, optionsRef, syncToServer]);

  useEffect(() => {
    if (!isSyncing && (!syncError || loadFailed)) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isSyncing, syncError, loadFailed]);

  const contextValue = useMemo<DocumentEditingOptionsContextValue>(
    () => ({
      options,
      updatedAt,
      updateTextbookOption,
      updateQuestionOption,
      resetTextbookOptions,
      resetQuestionOptions,
      resetAllOptions,
      replaceOptions,
      isSyncing,
      isHydrating,
      syncError,
      loadFailed,
      retrySync,
    }),
    [
      options,
      updatedAt,
      updateTextbookOption,
      updateQuestionOption,
      resetTextbookOptions,
      resetQuestionOptions,
      resetAllOptions,
      replaceOptions,
      isSyncing,
      isHydrating,
      syncError,
      loadFailed,
      retrySync,
    ]
  );

  return (
    <DocumentEditingOptionsContext.Provider value={contextValue}>
      {children}
    </DocumentEditingOptionsContext.Provider>
  );
}

export function useDocumentEditingOptions(): DocumentEditingOptionsContextValue {
  const ctx = useContext(DocumentEditingOptionsContext);
  if (!ctx) {
    throw new Error(
      'useDocumentEditingOptions must be used within a DocumentEditingOptionsProvider'
    );
  }
  return ctx;
}
